// React and related imports
import { useEffect, useState, useRef } from 'react';

// Redux selectors, hooks, and actions
import { useAppSelector } from '../redux/hooks/useAppSelector';
import {
  selectIsSessionModalOpen,
  selectSelectedSession,
} from '../redux/selectors/sessionSelectors';

// Types and Interfaces
import { IMultiLanguageValue } from '../common/interfaces/MultiLanguageValue.interface';
import { SessionStatus } from '../common/enums/SessionStatus';
import { SessionAssignTo } from '../common/enums/SessionAssignTo';
import { ISession } from '../common/interfaces/Session.interface';

// Extend ISession with assignTo and userIds for form state (required for add mode)
export interface SessionState extends Partial<ISession> {
  title: IMultiLanguageValue[];
  description: IMultiLanguageValue[];
  url: string;
  status: SessionStatus;
  assignTo: SessionAssignTo; // Required - always has default
  userIds: string[]; // Required - always has default
}

interface UseSessionStateReturn {
  isModalOpen: boolean;
  sessionState: SessionState | null;
  initialSessionSnapshot: SessionState | null;
  updateField: (
    field: keyof SessionState,
    value:
      | string
      | SessionStatus
      | SessionAssignTo
      | IMultiLanguageValue[]
      | string[]
  ) => void;
}

export const useSessionState = (): UseSessionStateReturn => {
  const isModalOpen = useAppSelector(selectIsSessionModalOpen);
  const selectedSession = useAppSelector(selectSelectedSession);

  const [sessionState, setSessionState] = useState<SessionState | null>(null);
  const initialSnapshotRef = useRef<SessionState | null>(null);

  useEffect(() => {
    if (!isModalOpen) return;

    // For edit mode: use selected session with default assignTo/userIds
    if (selectedSession) {
      const initialState = {
        ...selectedSession,
        assignTo: SessionAssignTo.All,
        userIds: [],
      };
      setSessionState(initialState);
      initialSnapshotRef.current = initialState;
    } else {
      // For add mode: initialize with defaults
      const initialState = {
        title: [
          { language: 'en', value: '' },
          { language: 'ar', value: '' },
        ],
        description: [
          { language: 'en', value: '' },
          { language: 'ar', value: '' },
        ],
        url: '',
        status: SessionStatus.ACTIVE,
        assignTo: SessionAssignTo.All,
        userIds: [],
      };
      setSessionState(initialState);
      initialSnapshotRef.current = initialState;
    }
  }, [isModalOpen, selectedSession]);

  const updateField = (
    field: keyof SessionState,
    value:
      | string
      | SessionStatus
      | SessionAssignTo
      | IMultiLanguageValue[]
      | string[]
  ) => {
    setSessionState((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        [field]: value,
      };
    });
  };

  return {
    isModalOpen,
    sessionState,
    initialSessionSnapshot: initialSnapshotRef.current,
    updateField,
  };
};
